import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, AuthResponse } from '../services/api';

interface User {
  id: string;
  email: string;
  name?: string;
  balance: number;
  createdAt: string;
  updatedAt: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && authApi.isAuthenticated();

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (authApi.isAuthenticated()) {
          // Try to fetch user profile to validate token
          const userData = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/user/profile`, {
            headers: {
              Authorization: `Bearer ${authApi.getToken()}`,
            },
          });
          
          if (userData.ok) {
            const response = await userData.json();
            if (response.success) {
              setUser(response.data);
            } else {
              // Invalid token, clear it
              authApi.logout();
            }
          } else {
            // Invalid token, clear it
            authApi.logout();
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        authApi.logout();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response: AuthResponse = await authApi.login({ email, password });
      setUser(response.user);
    } catch (error) {
      throw error;
    }
  };

  const register = async (email: string, password: string, name?: string) => {
    try {
      const response: AuthResponse = await authApi.register({ email, password, name });
      setUser(response.user);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    authApi.logout();
    // Remove any cross-user state that might have been written before
    // localStorage keys were scoped per user. Per-user scoped keys remain
    // (they're keyed on userId so they can't leak to another account).
    localStorage.removeItem('calendarCurrentDate');
    localStorage.removeItem('lastCurrentBalance');
    localStorage.removeItem('historicalBaseBalance');
    setUser(null);
  };

  const updateUser = (userData: User) => {
    setUser(userData);
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};